'use client';

import DistanceToNow from '#/components/DistanceToNow';
import EthereumBlockNumber from '#/components/EthereumBlockNumber';
import EthereumHexString from '#/components/EthereumHexString';
import { Alert, AlertDescription, AlertTitle } from '#/components/ui/alert';
import { Badge } from '#/components/ui/badge';
import { Button } from '#/components/ui/button';
import { DialogClose, DialogFooter } from '#/components/ui/dialog';
import { CopyToClipboard } from '#/components/v2/copy-to-clipboard';
import { useTranslation } from '#/contexts/useTranslation';
import { useConfiguredBlockExplorer } from '#/data/wagmi/configured';
import { ActivationQueueLength } from '#/modules/queue-activation';
import { useActor } from '@xstate/react';
import { ethers } from 'ethers';
import { Translation } from 'intl-lib';
import {
  CheckCircle,
  Copy,
  ExternalLink,
  Loader2,
  XCircle,
} from 'lucide-react';
import Link from 'next/link';
import { useContext } from 'react';
import { formatEther } from 'viem';
import {
  useBlock,
  useTransaction,
  useTransactionConfirmations,
  useWaitForTransactionReceipt,
} from 'wagmi';
import { MachineContext } from './contexts/machine';

export function StakingDialogTransaction() {
  const translation = useTranslation(
    translation => translation.EthereumStaking
  );
  const translationStakingAction = useTranslation(
    translation => translation.EthereumStaking.StakingAction
  );
  const translationApplication = useTranslation(
    translation => translation.Application
  );

  const blockExplorer = useConfiguredBlockExplorer();

  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const { transactionHash } = state.context;

  const { data: transaction, isLoading } = useTransaction({
    hash: transactionHash,
  });
  const { data: confirmations } = useTransactionConfirmations({
    hash: transactionHash,
  });
  const { data: transactionReceipt, isLoading: isTransactionReceiptLoading } =
    useWaitForTransactionReceipt({
      hash: transactionHash,
    });
  const { data: block, isLoading: isBlockLoading } = useBlock({
    blockNumber: transaction?.blockNumber,
    query: {
      enabled: transaction?.blockNumber != null,
    },
  });
  return (
    <>
      <div className="flex flex-col gap-4 py-4">
        <div className="flex items-center justify-between">
          <div className="px-4 text-sm">Transaction Hash</div>
          <div className="px-4 text-sm">
            <div className="flex items-center gap-1">
              <Button
                variant="link"
                size="sm"
                className="px-0 font-mono underline decoration-dashed decoration-gray-300 underline-offset-4 h-5"
                asChild
              >
                <Link
                  href={`${blockExplorer?.url}/tx/${transactionHash}`}
                  target="_blank"
                  rel="noreferrer"
                  prefetch={false}
                >
                  <EthereumHexString value={transactionHash} />
                </Link>
              </Button>
              <CopyToClipboard text={transactionHash ?? ''}>
                <Button variant="ghost" size="sm" className="w-5 h-5">
                  <Copy />
                </Button>
              </CopyToClipboard>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="px-4 text-sm">Status</div>
          <div className="px-4 text-sm">
            {transactionReceipt?.status != null ? (
              transactionReceipt.status === 'reverted' ? (
                <Badge
                  variant="outline"
                  className="text-red-600 border-red-300 bg-red-50"
                >
                  <XCircle className="mr-1 w-3 h-3 inline-block" />
                  {translation.TransactionDialog.FailStatus}
                </Badge>
              ) : (
                // <div className="rounded-md border border-red-300 bg-red-50 text-sm text-red-600 py-0.5 px-2 w-fit">
                //   <XCircle className="mr-1 w-3 h-3 inline-block" />
                //   {translation.TransactionDialog.FailStatus}
                // </div>
                <Badge
                  variant="outline"
                  className="text-green-600 border-green-300 bg-green-50"
                >
                  <CheckCircle className="mr-1 w-3 h-3 inline-block" />
                  {translation.TransactionDialog.SuccessStatus}
                </Badge>
                // <div className="rounded-md border border-green-300 bg-green-50 text-sm text-green-600 py-0.5 px-2 w-fit">
                //   <CheckCircle className="mr-1 w-3 h-3 inline-block" />
                //   {translation.TransactionDialog.SuccessStatus}
                // </div>
              )
            ) : (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="px-4 text-sm">Block</div>
          <div className="px-4 text-sm">
            {transaction?.blockNumber != null && confirmations != null ? (
              <div className="flex gap-2 items-center">
                <EthereumBlockNumber
                  blockNumber={transaction.blockNumber}
                  className="h-5"
                />
                <Badge>{String(confirmations)} Block Confirmations</Badge>
              </div>
            ) : (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="px-4 text-sm">ETH Staked</div>
          <div className="px-4 text-sm">
            {transaction != null ? formatEther(transaction.value) : '-'} ETH
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="px-4 text-sm">Activation Queue Length</div>
          <div className="px-4 text-sm">
            <ActivationQueueLength />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="px-4 text-sm">Credential Type</div>
          <div className="px-4 text-sm">
            {translationStakingAction.FullCompounding.Label}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="px-4 text-sm">Timestamp</div>
          <div className="px-4 text-sm">
            {block?.timestamp != null ? (
              <DistanceToNow date={Number(block.timestamp) * 1000} />
            ) : (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="px-4 text-sm">Transaction Fee</div>
          <div className="px-4 text-sm">
            {transaction &&
            transactionReceipt &&
            transaction.gasPrice &&
            transactionReceipt.gasUsed ? (
              `${ethers.utils.commify(
                formatEther(transaction.gasPrice * transactionReceipt.gasUsed)
              )} ETH`
            ) : (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
          </div>
        </div>
        <Alert className="bg-blue-50 hidden">
          <AlertTitle>
            Stay in touch for the latest updates—follow us!
          </AlertTitle>
          <AlertDescription></AlertDescription>
        </Alert>
      </div>
      <DialogFooter className="gap-2 sm:space-x-0">
        <DialogClose asChild>
          <Button variant="secondary">
            {translationApplication.Dialog.CloseButtonLabel}
          </Button>
        </DialogClose>
        <Button asChild variant="primary">
          <Link
            href={`${blockExplorer?.url}/tx/${transactionHash}`}
            target="_blank"
            prefetch={false}
          >
            <ExternalLink className="w-4 h-4" />
            <Translation
              path="global.noun.view_on_button_label"
              params={blockExplorer}
            />
          </Link>
        </Button>
      </DialogFooter>
    </>
  );
}
